import React from 'react';
import DialogForm from 'Components/Core/DialogForm';
import FormElement from 'Components/Core/Form/FormElement';

// Watchlist-Auswahl (Gruppen) fuer MarketdataWatchlist.js und
// MarketdataQuotes.js - bewusst als eigene, in sich geschlossene Komponente
// (haelt Gruppenliste/Dialog selbst), damit beide Module identisch aussehen
// und sich verhalten, ohne Logik zu duplizieren. Meldet die Auswahl nur nach
// aussen (onSelect), der Elternkomponente bleibt uebrig, ihre eigene Liste/
// Live-Ansicht bei einem Wechsel neu zu laden.
//
// Eigenes Dropdown statt eines nativen <select> - <option>-Elemente koennen
// kein Hover-Icon fuer die Eigenschaften (Name, max. Charts) zeigen
// (Browser-natives UI, kein CSS/JS-Zugriff auf einzelne Optionen).
// inline=true: schlanke Variante rechts im Filterfeld der Live-Ansicht
// (MarketdataQuotes) - zeigt den aktiven Watchlist-Namen als Hinweis plus ein
// Auswahl-Icon (gleiches Dropdown). Aus der mobil zu vollen Kopfzeile hierher
// verlegt (Chat vom 2026-08-12). Umschalten bleibt moeglich.
export default function MarketdataGroupSelector({ x, id, onSelect, forcedGroup, inline = false }) {

	const wafId = id + '-group';
	const dialogFormId = 'form-group-' + id;
	const [groups, setGroups] = React.useState([]);
	const [selectedGroupId, setSelectedGroupId] = React.useState(null);
	const [open, setOpen] = React.useState(false);
	const containerRef = React.useRef(null);

	function loadGroups(onDone) {
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataWatchlistGroupList', {}, (result) => {
			if (result.data.returncode !== 1) {
				return;
			}
			const rows = result.data.content || [];
			setGroups(rows);
			// Beim ersten Laden (bzw. wenn die bisherige Auswahl nicht mehr
			// existiert) die als Default markierte Watchlist bevorzugen, sonst
			// erste in der Sortierung - Live/Charts sollen sofort die
			// Default-Watchlist zeigen, ohne dass der Nutzer erst umschalten muss.
			setSelectedGroupId((current) => {
				if (current && rows.some((row) => row.groupid === current)) {
					return current;
				}
				const defaultGroup = rows.find((row) => Number(row.isdefault) === 1);
				return defaultGroup?.groupid ?? rows[0]?.groupid ?? null;
			});
			if (onDone) onDone(rows);
		});
	}

	function setDefaultGroup(group) {
		if (Number(group.isdefault) === 1) {
			return;
		}
		x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataWatchlistGroupSetDefault', { id: group.groupid }, (result) => {
			if (result.data.returncode === 1) {
				loadGroups();
			}
		});
	}

	React.useEffect(() => {
		loadGroups();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	React.useEffect(() => {
		if (selectedGroupId !== null) {
			onSelect(selectedGroupId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedGroupId]);

	// Erzwungener Wechsel von aussen (z.B. Stift-Icon in MarketdataQuotes.js:
	// "Watchlist bearbeiten" soll dieselbe Watchlist oeffnen, die in Live
	// gerade angezeigt wird, statt immer auf die Default-Watchlist
	// zurueckzufallen). "forcedGroup" ist bewusst {groupId, token} statt nur
	// die Id - ein Token sorgt dafuer, dass der Effekt auch dann erneut
	// greift, wenn zweimal hintereinander dieselbe Watchlist erzwungen wird.
	React.useEffect(() => {
		if (forcedGroup?.groupId != null) {
			setSelectedGroupId(forcedGroup.groupId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forcedGroup]);

	// Dropdown bei Klick ausserhalb schliessen.
	React.useEffect(() => {
		if (!open) {
			return;
		}
		function handleClickOutside(e) {
			if (containerRef.current && !containerRef.current.contains(e.target)) {
				setOpen(false);
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	function selectGroup(groupId) {
		setSelectedGroupId(groupId);
		setOpen(false);
	}

	function openCreateDialog() {
		x.waf.setAddSend(wafId, 'id', 0);
		x.waf.set(wafId, 'name', '');
		x.waf.set(wafId, 'maxcharts', '');
		x.wad[dialogFormId].setTitle('Neue Watchlist anlegen');
		x.wad[dialogFormId].show(() => { x.waf.focus(wafId, 'name') });
		setOpen(false);
	}

	// Watchlist samt enthaltener Symbole loeschen (siehe
	// MarketdataWatchlistGroupDelete.inc) - erst per x.wau.yesNo bestaetigen
	// lassen, das Backend verweigert zudem das Loeschen der letzten
	// verbleibenden Watchlist.
	function deleteGroup(group) {
		setOpen(false);
		x.wau.yesNo('Watchlist wirklich löschen?', (result) => {
			if (result !== 1) {
				return;
			}
			x.was.axPost('Backend/webapp.php?module=Marketdata&task=MarketdataWatchlistGroupDelete', { id: group.groupid }, (result) => {
				if (result.data.returncode !== 1) {
					x.wah.addHint(result.data.content?.message || 'Watchlist konnte nicht gelöscht werden.');
					return;
				}
				if (selectedGroupId === group.groupid) {
					setSelectedGroupId(null);
				}
				loadGroups();
			});
		}, true);
	}

	function openPropertiesDialog(group) {
		x.waf.setAddSend(wafId, 'id', group.groupid);
		x.waf.set(wafId, 'name', group.name);
		x.waf.set(wafId, 'maxcharts', group.maxcharts ?? '');
		x.wad[dialogFormId].setTitle('Watchlist-Eigenschaften');
		x.wad[dialogFormId].show(() => { x.waf.focus(wafId, 'name') });
		setOpen(false);
	}

	// Nach dem Anlegen die neue Watchlist gleich auswaehlen (result.content.groupid
	// ist nur beim Anlegen gesetzt, siehe MarketdataWatchlistGroupSave.inc) -
	// beim Umbenennen bleibt die Auswahl unveraendert, es wird nur der Name aktuell.
	function afterSend(result) {
		if (result.returncode !== 1) {
			return;
		}
		loadGroups((rows) => {
			if (result.content?.groupid) {
				const created = rows.find((row) => Number(row.groupid) === Number(result.content.groupid));
				if (created) {
					setSelectedGroupId(created.groupid);
				}
			}
		});
	}

	const currentGroup = groups.find((group) => group.groupid === selectedGroupId);

	return (
		<span className="marketdata-group-selector" ref={containerRef}>
			<DialogForm
				id={dialogFormId}
				x={x}
				wafId={wafId}
				title="Watchlist"
				classes="marketdata-group-dialog"
				module="Marketdata"
				sendTask="MarketdataWatchlistGroupSave"
				onAfterSend={afterSend}
			>
				<FormElement type="text" x={x} wafId={wafId} id="name" mandatory={true} title="Name" fieldValue="" maxLength="100" />
				<FormElement
					type="text"
					x={x}
					wafId={wafId}
					id="maxcharts"
					mandatory={false}
					title="Max. Charts (leer = unbegrenzt)"
					fieldValue=""
					maxLength="4"
					onChange={(e) => {
						const digitsOnly = e.target.value.replace(/\D/g, '');
						if (digitsOnly !== e.target.value) {
							x.waf.set(wafId, 'maxcharts', digitsOnly);
						}
					}}
				/>
			</DialogForm>
			{inline
				? <button
						type="button"
						className="marketdata-group-inline"
						title="Watchlist wechseln"
						onClick={() => setOpen((current) => !current)}
					>
						<span className="marketdata-group-inline-label">{currentGroup?.name ?? '…'}</span>
						<i className="bi bi-collection marketdata-group-inline-icon" />
					</button>
				: <button type="button" className="marketdata-group-current" onClick={() => setOpen((current) => !current)}>
						<span>{currentGroup?.name ?? '…'}</span>
						<i className="bi bi-chevron-down" />
					</button>
			}
			{open &&
				<div className="marketdata-group-dropdown">
					<div className="marketdata-group-dropdown-item marketdata-group-dropdown-new" onClick={openCreateDialog}>
						<i className="bi bi-folder-plus" /> Neue Watchlist anlegen
					</div>
					<div className="marketdata-group-dropdown-separator" />
					{groups.map((group) => (
						<div
							key={group.groupid}
							className={'marketdata-group-dropdown-item' + (group.groupid === selectedGroupId ? ' marketdata-group-dropdown-item-active' : '')}
							onClick={() => selectGroup(group.groupid)}
						>
							<span>{group.name}</span>
							<span className="marketdata-group-dropdown-icons">
								<i
									className={'bi marketdata-group-default-icon ' + (Number(group.isdefault) === 1 ? 'bi-star-fill' : 'bi-star')}
									title={Number(group.isdefault) === 1 ? 'Default-Watchlist' : 'Als Default-Watchlist festlegen'}
									onClick={(e) => { e.stopPropagation(); setDefaultGroup(group); }}
								/>
								{/* In der Live-Ansicht (inline) nur Favoriten-Stern; Bearbeiten/
								    Loeschen bleiben der Verwaltung vorbehalten (Chat vom
								    2026-08-12). */}
								{!inline && <i className="bi bi-pencil marketdata-group-edit-icon" title="Eigenschaften" onClick={(e) => { e.stopPropagation(); openPropertiesDialog(group); }} />}
								{!inline && <i className="bi bi-trash marketdata-group-delete-icon" title="Watchlist löschen" onClick={(e) => { e.stopPropagation(); deleteGroup(group); }} />}
							</span>
						</div>
					))}
				</div>
			}
		</span>
	);
}
